// Payout accounting console (admin-portal, PayGate Phase 4): the chart of accounts,
// a batch's double-entry journal, and a tenant trial balance. Reached through the
// gateway at /api/v1/paygate/admin/accounting/* (JWT-authed, platform-role-guarded
// upstream). Amounts are minor units. The global axios interceptor attaches the token.
import axios from 'axios';

const API_V1 = (import.meta.env.VITE_API_URL || 'http://localhost:8000/api/v1').replace(
  /\/api(\/v1)?\/?$/,
  '/api/v1',
);
const BASE = `${API_V1}/paygate/admin/accounting`;

export interface Account {
  account_key: string;
  gl_code: string;
  name: string;
  account_type: string;
  normal_balance: 'debit' | 'credit';
  is_override: boolean;
  active: boolean;
}

export interface JournalLine {
  account_key: string;
  gl_code: string;
  direction: 'debit' | 'credit';
  amount: number;
  currency: string;
}
export interface JournalEntry {
  id: string;
  batch_id?: string;
  item_id?: string;
  event: string;
  source_system?: string;
  currency: string;
  amount: number;
  description?: string;
  occurred_at: string;
  lines: JournalLine[];
}

export interface TrialBalanceRow {
  gl_code: string;
  account_key: string;
  debit: number;
  credit: number;
  balance: number;
}

export interface PostResult {
  batch_id: string;
  tenant_id: string;
  items: number;
  entries_posted: number;
}

export async function listChart(tenantId?: string): Promise<Account[]> {
  const { data } = await axios.get(`${BASE}/chart`, { params: tenantId ? { tenantId } : {} });
  return data.accounts ?? [];
}

export async function upsertAccount(input: {
  tenant_id?: string | null;
  account_key: string;
  gl_code: string;
  name: string;
  account_type: string;
  normal_balance: 'debit' | 'credit';
}): Promise<void> {
  await axios.put(`${BASE}/chart`, input);
}

export async function postBatch(batchId: string): Promise<PostResult> {
  const { data } = await axios.post(`${BASE}/batches/${batchId}/post`);
  return data;
}

export async function batchJournal(batchId: string): Promise<JournalEntry[]> {
  const { data } = await axios.get(`${BASE}/batches/${batchId}/journal`);
  return data.entries ?? [];
}

export async function trialBalance(
  tenantId: string,
  from?: string,
  to?: string,
): Promise<TrialBalanceRow[]> {
  const params: Record<string, string> = { tenantId };
  if (from) params.from = from;
  if (to) params.to = to;
  const { data } = await axios.get(`${BASE}/trial-balance`, { params });
  return data.rows ?? [];
}
